// Ortak e-Fatura mükellef sorgusu.
// Aktif e-Fatura entegrasyonuna göre sağlayıcı seçilir:
//   finansbank / qnb  -> eski QNBESolutions ucu (kimlik entegrasyon kaydından)
//   diğerleri (edm, pavopay, ...) -> einvoice servisi EDM_MUKELLEF_SORGULA (kimlik sunucuda)
import { ACCOUNTING_API, EINVOICE_API, handleApiError } from '../../../../config/api';
import { accountingApi, einvoiceApi } from '../../../../config/apiClient';

export interface ActiveEInvoiceIntegration {
  id: number;
  username: string;
  password: string;
  providerType: string;
  isActive: boolean;
}

export type TaxpayerLookupResult =
  | { status: 'no-integration' }
  | { status: 'invalid' }
  | { status: 'found'; title: string; alias: string; aliasCreationTime: string; provider: string }
  | { status: 'not-einvoice'; title?: string; provider: string }
  | { status: 'error'; message: string; provider?: string };

const PROVIDER_LABELS: Record<string, string> = {
  finansbank: 'QNB eFinans',
  qnb: 'QNB eFinans',
  edm: 'EDM',
  pavopay: 'PavoPay',
};

export const providerLabel = (providerType?: string): string => {
  const key = (providerType || '').toLowerCase();
  return PROVIDER_LABELS[key] || (providerType ? providerType.toUpperCase() : '');
};

export const isValidVknTckn = (value: string): boolean => /^[0-9]{10,11}$/.test((value || '').trim());

/** Aktif entegrasyonu döndürür; yoksa null. Hata olursa fırlatır. */
export const loadActiveEInvoiceIntegration = async (): Promise<ActiveEInvoiceIntegration | null> => {
  const response = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_LIST(false));
  const payload = response.data;
  const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  return list.find((i: ActiveEInvoiceIntegration) => i?.isActive) || null;
};

const lookupQnb = async (integration: ActiveEInvoiceIntegration, vkn: string): Promise<TaxpayerLookupResult> => {
  const provider = providerLabel(integration.providerType);
  const baseUrl = process.env.REACT_APP_EINVOICE_URL;
  if (!baseUrl) return { status: 'error', message: 'REACT_APP_EINVOICE_URL', provider };

  const response = await fetch(
    `${baseUrl}/QNBESolutions/EFaturaKullaniciBilgisi?username=${encodeURIComponent(integration.username)}&password=${encodeURIComponent(integration.password)}&sorguVkn=${encodeURIComponent(vkn)}`,
    { method: 'GET', headers: { 'Content-Type': 'application/json' } }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    return { status: 'error', message: errorData.Error || errorData.message || `HTTP ${response.status}`, provider };
  }
  const data = await response.json();
  const users = Array.isArray(data?.kullanicilar) ? data.kullanicilar : [];
  if (data?.success && data?.eFaturaMukellefMi && users.length > 0) {
    const first = users[0] || {};
    return {
      status: 'found',
      title: first.unvan || '',
      alias: first.etiket || '',
      aliasCreationTime: first.kayitZamani || '',
      provider,
    };
  }
  if (data?.success) return { status: 'not-einvoice', provider };
  return { status: 'error', message: data?.message || data?.Error || '', provider };
};

const lookupEdm = async (integration: ActiveEInvoiceIntegration, vkn: string): Promise<TaxpayerLookupResult> => {
  const provider = providerLabel(integration.providerType);
  const resp = await einvoiceApi.post(EINVOICE_API.EDM_MUKELLEF_SORGULA, { vknTckn: vkn });
  const data = resp.data || {};
  const users = Array.isArray(data.users) ? data.users : [];
  const first = users[0] || {};
  if (data.found && data.isEInvoiceUser) {
    return {
      status: 'found',
      title: first.title || data.title || '',
      alias: first.alias || '',
      aliasCreationTime: first.aliasCreationTime || '',
      provider,
    };
  }
  return { status: 'not-einvoice', title: data.title || undefined, provider };
};

/**
 * VKN/TCKN için mükellef sorgusu. Entegrasyon verilmezse aktif entegrasyon yüklenir.
 * Hiçbir durumda fırlatmaz; sonucu status ile döndürür.
 */
export const lookupTaxpayer = async (
  vknTckn: string,
  integration?: ActiveEInvoiceIntegration | null
): Promise<TaxpayerLookupResult> => {
  const vkn = (vknTckn || '').trim();
  if (!isValidVknTckn(vkn)) return { status: 'invalid' };

  let active = integration;
  try {
    if (active === undefined) active = await loadActiveEInvoiceIntegration();
  } catch (err) {
    return { status: 'error', message: handleApiError(err) };
  }
  if (!active) return { status: 'no-integration' };

  const provider = (active.providerType || '').toLowerCase();
  try {
    if (provider === 'finansbank' || provider === 'qnb') return await lookupQnb(active, vkn);
    return await lookupEdm(active, vkn);
  } catch (err: any) {
    const respError = err?.response?.data?.error;
    return { status: 'error', message: respError || handleApiError(err), provider: providerLabel(active.providerType) };
  }
};
